import { SyntheticEvent } from "react";
import { Autocomplete, Box, Chip, CircularProgress, TextField, useTheme } from "@mui/material";
import { resolveCodeChipPalette } from "@/components/common/fields/codeChipColors";
import { RefCodeOption, RefCodeType, useRefCodeSearch } from "@/hooks/useRefCodeSearch";

export interface AutocompleteCodeInputProps {
  /** Ref-code domain — drives both the API endpoint and chip color. */
  codeType: RefCodeType;
  /** Currently selected codes (array of raw code strings, e.g. ["E11.65", "E11.9"]). */
  value: string[];
  /** Called whenever the selected set changes. */
  onChange: (codes: string[]) => void;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  /**
   * Per-character limit enforced on typed free-text entries (mirrors
   * `EditableCodeChipSection`'s `maxLength` prop, used for DB CHAR(n) parity).
   * Does not affect values coming from the typeahead dropdown.
   */
  maxLength?: number;
}

/** Normalize a raw user-typed code before committing to the selection. */
const normalizeFreeText = (raw: string, maxLength?: number): string => {
  const trimmed = raw.trim().toUpperCase();
  if (!trimmed) return "";
  return maxLength ? trimmed.slice(0, maxLength) : trimmed;
};

/**
 * MUI Autocomplete wired to `useRefCodeSearch` for typeahead against the
 * `/ref-codes/{codeType}` API. Supports:
 *
 * - Multi-select with styled chips (palette per codeType)
 * - Free-text fallback (`freeSolo`) so users can enter codes that aren't in
 *   the ref tables — essential while CPT/NDC seeding is still in flight
 * - Graceful no-op on duplicate entries
 *
 * This component owns the input + chips only. Section headers, collapse UI,
 * and Edit/Done toggles remain the caller's concern (see EditableCodeChipSection).
 */
export const AutocompleteCodeInput = ({
  codeType,
  value,
  onChange,
  label,
  placeholder = "Type to search…",
  disabled = false,
  maxLength,
}: AutocompleteCodeInputProps) => {
  const theme = useTheme();
  const palette = resolveCodeChipPalette(codeType);
  // The hook's `search` is only used to debounce the API query — MUI Autocomplete
  // manages the displayed input text internally. We feed typing into the hook via
  // `onInputChange` but don't bind `inputValue` so the input stays uncontrolled.
  const { options, loading, setSearch } = useRefCodeSearch(codeType);

  /**
   * Autocomplete's value in multiple+freeSolo mode is `Array<string | RefCodeOption>`.
   * We collapse that into a de-duplicated uppercase string array for the parent.
   */
  const handleChange = (_event: SyntheticEvent, next: Array<string | RefCodeOption>) => {
    const seen = new Set<string>();
    const result: string[] = [];
    for (const item of next) {
      const raw = typeof item === "string" ? normalizeFreeText(item, maxLength) : item.code;
      if (!raw || seen.has(raw)) continue;
      seen.add(raw);
      result.push(raw);
    }
    onChange(result);
  };

  /**
   * Render each selected value as a styled chip. Accepts both string (raw code)
   * and RefCodeOption (object selected from the dropdown) — in multi+freeSolo
   * Autocomplete can hand us either, so we normalize at the label boundary.
   */
  const renderTags = (tagValue: Array<string | RefCodeOption>, getTagProps: (arg: { index: number }) => object) =>
    tagValue.map((item, index) => {
      const code = typeof item === "string" ? item : item.code;
      return (
        <Chip
          {...getTagProps({ index })}
          key={code}
          label={code}
          size="small"
          sx={{
            bgcolor: palette.bg,
            color: palette.color,
            fontFamily: "monospace",
            fontSize: "0.75rem",
            fontWeight: 500,
            "& .MuiChip-deleteIcon": { color: palette.color, fontSize: 14 },
          }}
        />
      );
    });

  return (
    <Autocomplete<RefCodeOption, true, false, true>
      multiple
      freeSolo
      disabled={disabled}
      options={options}
      value={value}
      onInputChange={(_event, next) => setSearch(next)}
      onChange={handleChange}
      loading={loading}
      filterOptions={(x) => x} // API already filters; do not re-filter client-side
      getOptionLabel={(option) => (typeof option === "string" ? option : `${option.code} — ${option.description}`)}
      isOptionEqualToValue={(option, candidate) => {
        const a = typeof option === "string" ? option : option.code;
        const b = typeof candidate === "string" ? candidate : candidate.code;
        return a === b;
      }}
      renderOption={(props, option) => (
        <Box component="li" {...props} key={option.code}>
          <span style={{ fontFamily: "monospace", fontWeight: 500, marginRight: 8, color: theme.palette.text.primary }}>
            {option.code}
          </span>
          <span style={{ color: theme.palette.text.secondary }}>{option.description}</span>
        </Box>
      )}
      renderTags={renderTags}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={value.length === 0 ? placeholder : undefined}
          size="small"
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
            sx: { fontFamily: "monospace", fontSize: "0.75rem" },
          }}
        />
      )}
    />
  );
};
